<template>
  <div class="min-h-screen bg-white">
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-green-50 to-emerald-100 py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Actualités
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Suivez les dernières nouvelles d'Anamnesis et les avancées dans le domaine des sciences du patrimoine culturel
          </p>
        </div>
      </div>
    </section>

    <!-- Articles principaux -->
    <section class="py-20 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Article principal -->
          <article class="lg:col-span-2 bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200">
            <img 
              src="https://placeholderimage.eu/api/800/400?category=monument" 
              alt="Lancement d'Anamnesis" 
              class="w-full h-64 object-cover"
            />
            <div class="p-8">
              <div class="flex items-center mb-4">
                <time class="text-sm text-gray-500">15 Mars 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-sm text-blue-600 font-medium">Lancement</span>
              </div>
              <h2 class="text-3xl font-bold text-gray-900 mb-4">
                Lancement officiel de la plateforme Anamnesis
              </h2>
              <p class="text-lg text-gray-600 mb-6">
                Après plusieurs années de développement, la plateforme Anamnesis est officiellement lancée pour révolutionner la documentation patrimoniale. Cette nouvelle solution offre aux chercheurs et professionnels du patrimoine des outils innovants pour documenter, gérer et partager leurs données selon les principes FAIR.
              </p>
              <p class="text-gray-600 mb-6">
                Développée par le laboratoire MAP (UPR 2002 CNRS) en partenariat avec le CICRP, le LRMH et la Fondation Sciences du Patrimoine, Anamnesis répond aux besoins croissants de standardisation et d'interopérabilité dans le domaine des sciences du patrimoine culturel.
              </p>
              <a href="#" class="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium">
                Lire la suite
                <ArrowRightIcon class="w-4 h-4 ml-2" />
              </a>
            </div>
          </article>

          <!-- Articles secondaires -->
          <div class="space-y-8">
            <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200">
              <img 
                src="https://placeholderimage.eu/api/400/200?category=technology" 
                alt="Partenariat E-RIHS" 
                class="w-full h-32 object-cover"
              />
              <div class="p-6">
                <div class="flex items-center mb-3">
                  <time class="text-xs text-gray-500">10 Mars 2024</time>
                  <span class="mx-2 text-gray-300">•</span>
                  <span class="text-xs text-green-600 font-medium">Partenariat</span>
                </div>
                <h3 class="text-lg font-bold text-gray-900 mb-3">
                  Anamnesis rejoint l'infrastructure européenne E-RIHS
                </h3>
                <p class="text-sm text-gray-600 mb-4">
                  La plateforme Anamnesis devient un outil de référence au sein de l'infrastructure européenne E-RIHS pour les sciences du patrimoine.
                </p>
                <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  En savoir plus →
                </a>
              </div>
            </article>

            <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200">
              <img 
                src="https://placeholderimage.eu/api/400/200?category=people" 
                alt="Formation utilisateurs" 
                class="w-full h-32 object-cover"
              />
              <div class="p-6">
                <div class="flex items-center mb-3">
                  <time class="text-xs text-gray-500">5 Mars 2024</time>
                  <span class="mx-2 text-gray-300">•</span>
                  <span class="text-xs text-purple-600 font-medium">Formation</span>
                </div>
                <h3 class="text-lg font-bold text-gray-900 mb-3">
                  Première session de formation pour les utilisateurs
                </h3>
                <p class="text-sm text-gray-600 mb-4">
                  Une formation complète a été organisée pour accompagner les premiers utilisateurs dans la prise en main d'Anamnesis.
                </p>
                <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  En savoir plus →
                </a>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <!-- Autres articles -->
    <section class="py-20 bg-gray-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">
          Autres actualités
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <img 
              src="https://placeholderimage.eu/api/400/250?category=monument" 
              alt="Nouvelle fonctionnalité" 
              class="w-full h-48 object-cover"
            />
            <div class="p-6">
              <div class="flex items-center mb-3">
                <time class="text-xs text-gray-500">28 Février 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-xs text-blue-600 font-medium">Mise à jour</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-3">
                Nouvelle fonctionnalité de géolocalisation
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                Anamnesis intègre désormais un système de géolocalisation avancé pour une meilleure gestion des sites patrimoniaux.
              </p>
              <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Découvrir →
              </a>
            </div>
          </article>

          <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <img 
              src="https://placeholderimage.eu/api/400/250?category=technology" 
              alt="API ouverte" 
              class="w-full h-48 object-cover"
            />
            <div class="p-6">
              <div class="flex items-center mb-3">
                <time class="text-xs text-gray-500">20 Février 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-xs text-orange-600 font-medium">Développement</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-3">
                Ouverture de l'API Anamnesis
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                L'API d'Anamnesis est maintenant accessible aux développeurs pour créer des intégrations personnalisées.
              </p>
              <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Documentation →
              </a>
            </div>
          </article>

          <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <img 
              src="https://placeholderimage.eu/api/400/250?category=people" 
              alt="Conférence" 
              class="w-full h-48 object-cover"
            />
            <div class="p-6">
              <div class="flex items-center mb-3">
                <time class="text-xs text-gray-500">15 Février 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-xs text-red-600 font-medium">Événement</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-3">
                Présentation à la conférence CIDOC 2024
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                L'équipe Anamnesis présente les innovations de la plateforme lors de la conférence internationale CIDOC.
              </p>
              <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Voir la présentation →
              </a>
            </div>
          </article>

          <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <img 
              src="https://placeholderimage.eu/api/400/250?category=monument" 
              alt="Cas d'usage" 
              class="w-full h-48 object-cover"
            />
            <div class="p-6">
              <div class="flex items-center mb-3">
                <time class="text-xs text-gray-500">10 Février 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-xs text-green-600 font-medium">Étude de cas</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-3">
                Succès de l'implémentation au Louvre
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                Le musée du Louvre adopte Anamnesis pour la documentation de ses collections et restaurations.
              </p>
              <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Lire l'étude →
              </a>
            </div>
          </article>

          <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <img 
              src="https://placeholderimage.eu/api/400/250?category=technology" 
              alt="Mise à jour sécurité" 
              class="w-full h-48 object-cover"
            />
            <div class="p-6">
              <div class="flex items-center mb-3">
                <time class="text-xs text-gray-500">5 Février 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-xs text-yellow-600 font-medium">Sécurité</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-3">
                Renforcement de la sécurité des données
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                Nouvelles mesures de sécurité implémentées pour protéger les données sensibles du patrimoine culturel.
              </p>
              <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Détails techniques →
              </a>
            </div>
          </article>

          <article class="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200 hover:shadow-lg transition-shadow duration-300">
            <img 
              src="https://placeholderimage.eu/api/400/250?category=people" 
              alt="Communauté" 
              class="w-full h-48 object-cover"
            />
            <div class="p-6">
              <div class="flex items-center mb-3">
                <time class="text-xs text-gray-500">1 Février 2024</time>
                <span class="mx-2 text-gray-300">•</span>
                <span class="text-xs text-indigo-600 font-medium">Communauté</span>
              </div>
              <h3 class="text-lg font-bold text-gray-900 mb-3">
                Lancement du forum communautaire
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                Un espace d'échange dédié aux utilisateurs d'Anamnesis pour partager expériences et bonnes pratiques.
              </p>
              <a href="#" class="text-blue-600 hover:text-blue-700 text-sm font-medium">
                Rejoindre →
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Newsletter -->
    <section class="py-20 bg-blue-600">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl font-bold text-white mb-6">
          Restez informé des actualités Anamnesis
        </h2>
        <p class="text-xl text-blue-100 mb-8">
          Recevez les dernières nouvelles et mises à jour directement dans votre boîte mail
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
          <input 
            type="email" 
            placeholder="Votre adresse email"
            class="flex-1 px-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-blue-300 focus:outline-none"
          />
          <button class="bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg hover:bg-blue-800 transition-colors duration-300">
            S'abonner
          </button>
        </div>
      </div>
    </section>
  </div>
  <AppFooter />
</template>

<script lang="ts">
import AppFooter from '@/components/UI/AppFooter.vue';
import { ArrowRightIcon } from "lucide-vue-next";
import { defineComponent } from "vue";

export default defineComponent({
  name: "Actualites",
  components: {
    ArrowRightIcon,
    AppFooter
  }
});
</script>

<style scoped>
.hover\:shadow-lg:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:bg-blue-800:hover {
  background-color: #1e40af;
}
</style> 